import type {
  ApprovingInvoiceReportFormInputResponseInputAdditionalValuesItem,
  BusinessDocumentDocumentType,
  InvoiceRegistrationNumberFieldAdditionalValues,
  ReportBusinessDocument,
} from 'ap-openapi';
import { splitFileNameExtension } from './fileUtils';

const isValidBusinessDocumentType = (
  type: string
): type is BusinessDocumentDocumentType => {
  return [
    'quotation',
    'purchase_order',
    'contract',
    'delivery_note',
    'acceptance_certificate',
    'invoice',
    'receipt',
    'others',
    'not_specified',
  ].includes(type);
};

const isBusinessDocument = (
  document: ApprovingInvoiceReportFormInputResponseInputAdditionalValuesItem
): document is ReportBusinessDocument &
  InvoiceRegistrationNumberFieldAdditionalValues => {
  return 'document_type' in document && 'document_title' in document;
};

export interface ProcessedBusinessDocument
  extends ReportBusinessDocument,
    InvoiceRegistrationNumberFieldAdditionalValues {
  documentTypeKey: string;
  label: string;
  href: string;
  fileNameParts: { name: string; extension: string };
}

/**
 * Processes a single business document with all necessary metadata
 */
export const processBusinessDocument = (
  document: ReportBusinessDocument &
    InvoiceRegistrationNumberFieldAdditionalValues,
  documentTypeKey: string,
  documentTypeLabel: Record<string, string>,
  baseURL: string
): ProcessedBusinessDocument => {
  const fileNameParts = splitFileNameExtension(document?.document_title ?? '');

  return {
    ...document,
    documentTypeKey,
    label: documentTypeLabel[documentTypeKey] ?? '',
    href: `${baseURL}/business_documents?id=${document?.id}`,
    fileNameParts,
  };
};

/**
 * Processes an array of business documents
 */
export const processBusinessDocuments = (
  documents:
    | ApprovingInvoiceReportFormInputResponseInputAdditionalValuesItem[]
    | null
    | undefined,
  convertDocumentTypeKeyToValue: (key: BusinessDocumentDocumentType) => string,
  documentTypeLabel: Record<string, string>,
  baseURL: string
): ProcessedBusinessDocument[] => {
  if (!documents) return [];

  return documents.filter(isBusinessDocument).map((document) => {
    const documentType = document?.document_type ?? 'not_specified';
    const documentTypeKey = convertDocumentTypeKeyToValue(
      isValidBusinessDocumentType(documentType) ? documentType : 'not_specified'
    );

    return processBusinessDocument(
      document,
      documentTypeKey,
      documentTypeLabel,
      baseURL
    );
  });
};
